import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Spinner, getSupabase, useCurrentUser, cn } from '@dogdisk/shared'
import { AppLayout } from '../../components/app-layout'
import { MobileScreen, TopBar } from '../../components/mobile-screen'

type Prefs = {
  notify_transfer_received: boolean
  notify_pack_subscribed: boolean
  notify_vip_expiring: boolean
  notify_ticket_replied: boolean
  notify_message_received: boolean
}

const DEFAULTS: Prefs = {
  notify_transfer_received: true,
  notify_pack_subscribed: true,
  notify_vip_expiring: true,
  notify_ticket_replied: true,
  notify_message_received: true,
}

export function NotificationsPage() {
  const { user } = useCurrentUser()
  const [prefs, setPrefs] = useState<Prefs>(DEFAULTS)
  const [busy, setBusy] = useState(false)
  const [savedAt, setSavedAt] = useState<number | null>(null)
  const [allEnabled, setAllEnabled] = useState(true)

  const query = useQuery({
    queryKey: ['profile', 'prefs', user?.user_id] as const,
    enabled: Boolean(user),
    queryFn: async () => {
      if (!user) return null
      const sb = getSupabase()
      // eslint-disable-next-line no-restricted-syntax -- effective_notification_settings owner 收口(读自己的通知设置)
      const { data, error } = await sb
        .from('effective_notification_settings')
        .select('*')
        .eq('user_id', user.user_id)
        .maybeSingle()
      if (error) throw error
      return data as Prefs | null
    },
  })

  useEffect(() => {
    if (query.data) {
      setPrefs({ ...DEFAULTS, ...query.data })
      setAllEnabled(Object.values(query.data).every(Boolean))
    }
  }, [query.data])

  const update = (next: Prefs) => {
    setPrefs(next)
    if (user) {
      void (async () => {
        setBusy(true)
        try {
          const sb = getSupabase()
          await sb.from('profiles').update(next).eq('user_id', user.user_id)
          setSavedAt(Date.now())
        } finally {
          setBusy(false)
        }
      })()
    }
  }

  const toggleAll = () => {
    const v = !allEnabled
    setAllEnabled(v)
    const next: Prefs = {
      notify_transfer_received: v,
      notify_pack_subscribed: v,
      notify_vip_expiring: v,
      notify_ticket_replied: v,
      notify_message_received: v,
    }
    update(next)
  }

  return (
    <AppLayout>
      <MobileScreen>
        <TopBar title="通知设置" />

        <main data-testid="Y0dLm" className="flex flex-1 flex-col gap-5 px-6 pt-4 pb-12">
          {query.isLoading ? (
            <div className="flex justify-center py-12"><Spinner className="text-gray-400" /></div>
          ) : (
            <>
              <section>
                <p className="mb-2 text-[12px] text-[#9CA3AF]">推送通知</p>
                <ul>
                  <Toggle
                    icon="bell"
                    label="全部通知"
                    value={allEnabled}
                    onChange={toggleAll}
                    busy={busy}
                  />
                </ul>
              </section>

              <section>
                <p className="mb-2 text-[12px] text-[#9CA3AF]">消息通知</p>
                <ul className="flex flex-col gap-2">
                  <Toggle
                    icon="message"
                    label="私信通知"
                    value={prefs.notify_message_received}
                    onChange={(v) => update({ ...prefs, notify_message_received: v })}
                    busy={busy}
                  />
                  <Toggle
                    icon="at"
                    label="系统消息"
                    value={prefs.notify_transfer_received && prefs.notify_pack_subscribed && prefs.notify_vip_expiring && prefs.notify_ticket_replied}
                    onChange={(v) =>
                      update({
                        ...prefs,
                        notify_transfer_received: v,
                        notify_pack_subscribed: v,
                        notify_vip_expiring: v,
                        notify_ticket_replied: v,
                      })
                    }
                    busy={busy}
                  />
                </ul>
              </section>

              {savedAt && (
                <p className="text-center text-[12px] text-[#19caad]">
                  已保存 ({new Date(savedAt).toLocaleTimeString()})
                </p>
              )}
            </>
          )}
        </main>
      </MobileScreen>
    </AppLayout>
  )
}

function Toggle({
  icon,
  label,
  value,
  onChange,
  busy,
}: {
  icon: 'bell' | 'message' | 'at'
  label: string
  value: boolean
  onChange: (v: boolean) => void
  busy?: boolean
}) {
  return (
    <li className="flex items-center gap-3 rounded-2xl bg-[#F6F7F8] px-4 py-3">
      <span className="text-[#9CA3AF]">
        {icon === 'bell' && <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9" /></svg>}
        {icon === 'message' && <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /></svg>}
        {icon === 'at' && <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="4" /><path d="M16 12v1a3 3 0 005 0v-1a9 9 0 10-3.6 7.2" /></svg>}
      </span>
      <span className="flex-1 text-[14px] text-[#1A1A1A]">{label}</span>
      <button
        type="button"
        onClick={() => onChange(!value)}
        disabled={busy}
        aria-pressed={value}
        className={cn(
          'relative h-7 w-12 shrink-0 rounded-full transition-colors disabled:opacity-50',
          value ? 'bg-[#19caad]' : 'bg-[#E0E0E0]',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-transform',
            value ? 'translate-x-[22px]' : 'translate-x-0.5',
          )}
        />
      </button>
    </li>
  )
}
